import { Link } from 'react-router-dom';
import { LegalChrome } from './LegalChrome';
import { SUPPORT_EMAIL } from './constants';

const LAST_UPDATED = 'June 22, 2026';

export function SafetyPolicyPage() {
  return (
    <LegalChrome>
      <h1 className="legal-h1">Safety Policy</h1>
      <p className="legal-muted">Last updated: {LAST_UPDATED}</p>

      <p className="legal-p">
        Kynward, operated by Elyxora Works, LLC, is committed to helping adults meet through
        respectful, low-pressure conversation. This Safety Policy describes how we approach member
        safety, prohibited conduct, screening, reporting, and enforcement. It supplements our{' '}
        <Link className="legal-a" to="/legal/community-guidelines">
          Community Guidelines
        </Link>
        ,{' '}
        <Link className="legal-a" to="/legal/terms-of-service">
          Terms of Service
        </Link>
        , and{' '}
        <Link className="legal-a" to="/child-safety">
          Child Safety Standards
        </Link>
        .
      </p>

      <h2 className="legal-h2">Background screening disclosure</h2>
      <p className="legal-p" style={{ fontWeight: 700, textTransform: 'uppercase' }}>
        Kynward does not conduct criminal background checks on members before permitting them to
        communicate with other members through the Services.
      </p>
      <p className="legal-p">
        We do not exclude members from Kynward based on criminal conviction history through a formal
        background-screening program. We may take action against accounts that violate our policies
        or applicable law, including after reports, moderation review, or other safety signals.
      </p>

      <h2 className="legal-h2">Identity verification</h2>
      <p className="legal-p">
        Kynward may use age confirmation, account information, and technical signals to help enforce
        our 18+ requirement. We do not guarantee the identity, background, or intentions of any
        member. You are responsible for your own safety decisions when using Kynward.
      </p>

      <h2 className="legal-h2">Prohibited conduct</h2>
      <p className="legal-p">
        The following conduct is prohibited on Kynward and may result in removal, restriction, or
        account termination:
      </p>
      <ul className="legal-ul">
        <li className="legal-li">
          harassment, hate, threats, bullying, or repeated unwanted contact
        </li>
        <li className="legal-li">
          illegal content, sexual content involving minors, non-consensual imagery, or content that
          promotes violence or exploitation
        </li>
        <li className="legal-li">scams, spam, impersonation, or commercial solicitation</li>
        <li className="legal-li">
          pressure to share contact information, meet in person, or continue interactions against
          someone&apos;s wishes
        </li>
        <li className="legal-li">
          misuse of matching, messaging, reporting, or safety features, including automation or
          scraping
        </li>
        <li className="legal-li">
          any conduct that threatens public or personal safety or violates our Community Guidelines
          or Terms of Service
        </li>
      </ul>
      <p className="legal-p">
        For the full community standards, see our{' '}
        <Link className="legal-a" to="/legal/community-guidelines">
          Community Guidelines
        </Link>
        .
      </p>

      <h2 className="legal-h2">Reporting and blocking</h2>
      <p className="legal-p">
        You can report profiles, messages, and other safety concerns directly in the Kynward app.
        You can also block other members to stop further contact. Reports involving child safety,
        threats, or sexual exploitation are reviewed with priority.
      </p>
      <p className="legal-p">
        For urgent matters involving imminent danger, contact local emergency services. For child
        safety concerns, see our{' '}
        <Link className="legal-a" to="/child-safety">
          Child Safety Standards
        </Link>
        .
      </p>

      <h2 className="legal-h2">Notice to other members after a report</h2>
      <p className="legal-p">
        Kynward generally does not notify other members that a report has been filed against them
        while a review is in progress. If we take action on an account, affected members may notice
        that content was removed, features were restricted, or the account is no longer available.
        We do not routinely disclose report details to other users.
      </p>

      <h2 className="legal-h2">Enforcement</h2>
      <p className="legal-p">
        We may warn, restrict, suspend, or permanently remove accounts that violate our policies or
        create safety risk. We may preserve records and cooperate with law enforcement where
        required or appropriate. Severe violations, including child safety issues, may result in
        immediate termination and reporting to authorities.
      </p>

      <h2 className="legal-h2">Safety awareness</h2>
      <p className="legal-p">
        Online and in-person interactions carry risk. The following practices can help you use
        Kynward more safely:
      </p>
      <ul className="legal-ul">
        <li className="legal-li">
          Anyone who can commit identity theft can also misrepresent themselves online. Be cautious
          when communicating with people you have not met in person.
        </li>
        <li className="legal-li">
          Never include your last name, email address, home address, phone number, place of work, or
          other identifying information in your public profile or early messages. Stop communicating
          with anyone who pressures you for personal or financial information or tries to trick you
          into revealing it.
        </li>
        <li className="legal-li">
          If you choose to meet someone in person, tell a friend or family member where you are
          going and when you expect to return. Do not agree to be picked up at your home. Provide
          your own transportation and meet in a public place with other people around.
        </li>
        <li className="legal-li">
          Use Kynward&apos;s in-app meetup and safety-contact features when available, and trust
          your instincts. You can leave a conversation or decline a meetup at any time.
        </li>
      </ul>

      <h2 className="legal-h2">Appeals</h2>
      <p className="legal-p">
        If your account or access was restricted and you believe we made a mistake, contact us at{' '}
        <a className="legal-a" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>{' '}
        with the email address associated with your account. We will review appeals where
        appropriate, but we are not obligated to restore access.
      </p>

      <h2 className="legal-h2">Related policies</h2>
      <p className="legal-p">
        <Link className="legal-a" to="/legal/community-guidelines">
          Community Guidelines
        </Link>
        {' · '}
        <Link className="legal-a" to="/legal/terms-of-service">
          Terms of Service
        </Link>
        {' · '}
        <Link className="legal-a" to="/legal/privacy-policy">
          Privacy Policy
        </Link>
        {' · '}
        <Link className="legal-a" to="/child-safety">
          Child Safety Standards
        </Link>
      </p>
    </LegalChrome>
  );
}
